import { Box, makeStyles } from '@material-ui/core'
import { Page } from 'react-pdf'
import { BoundingBoxSelector } from './BoundingBoxSelector'
import { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import PdfDocumentWrapper from '../../../../PdfDocumentWrapper'
import { PDFPage } from './PDFPage'
import { RegionsOfInterestDisplayLayer } from './RegionsOfInterestDisplayLayer'
import { UIRegionOfInterest } from '../../UIRegionOfInterest'
import { useSelectedFilesContext } from '../../../../selectedFilesProvider'
import { ScheduleType } from '../../../../../../shared/src/data/scheduleType'
import { ViewType } from '../../../viewType'
import { useSelectedColumnsContext } from '../../../../SelectedColumnsProvider'

const DEFAULT_BOX_WIDTH = 1000

const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
}))

type PDFViewportBoxProps = {
  selectedFile: PdfDocumentWrapper
  selectedPageNumber: number
  isInSelectionMode: boolean
  regionOfInterestContentType: ViewType[]
  selectedLabel: ScheduleType
  appendSelectedRegionOfInterest: (
    uiRegionOfInterest: UIRegionOfInterest,
  ) => void
  focusedRegionOfInterest: UIRegionOfInterest | null
  zoom: number
  setZoom: (zoom: number) => void
}

export default function PDFViewport({
  selectedFile,
  selectedPageNumber,
  isInSelectionMode,
  regionOfInterestContentType,
  selectedLabel,
  appendSelectedRegionOfInterest,
  focusedRegionOfInterest,
  zoom,
  setZoom,
}: PDFViewportBoxProps) {
  const classes = useStyles()
  const [canvasWidth, setCanvasWidth] = useState<number>(0)
  const [canvasHeight, setCanvasHeight] = useState<number>(0)

  const updateZoom = useCallback(() => {
    const availableWidth = window.innerWidth * 0.55
    const calculatedZoom = availableWidth / canvasWidth
    setZoom(Math.round(calculatedZoom * 4) / 4)
  }, [canvasWidth, setZoom])
  
  useEffect(() => {
    window.addEventListener('resize', updateZoom)
    return () => {
      window.removeEventListener('resize', updateZoom)
    }
  }, [updateZoom])

  const onPageLoaded = (page: PDFPage) => {
    const viewport = page.getViewport({ scale: 1 });
    const originalWidth = viewport.width;
    const scale = DEFAULT_BOX_WIDTH / originalWidth;
    const scaledViewport = page.getViewport({ scale });
    
    setCanvasWidth(scaledViewport.width);
    setCanvasHeight(scaledViewport.height);
  }

  useEffect(() => {
    if (canvasWidth > 0) {
      updateZoom();
    }
  }, [canvasWidth, updateZoom]);

  const { pageToFileMap } = useSelectedFilesContext();
  const { originalPageNumber } = pageToFileMap.get(selectedPageNumber)!
  const { getRegionsForPage } = useSelectedColumnsContext()
  return (
    <Box
      height={canvasHeight * zoom}
      width={canvasWidth * zoom}
      minWidth={canvasWidth * zoom}
      className={classes.margin}
    >
      <Box position='absolute'>
        <Page
          pageNumber={originalPageNumber}
          width={DEFAULT_BOX_WIDTH}
          scale={zoom}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          /*@ts-ignore*/
          pdf={selectedFile.document}
          onLoadSuccess={onPageLoaded}
        />
      </Box>
      <BoundingBoxSelector
        label={selectedLabel}
        width={canvasWidth * zoom}
        height={canvasHeight * zoom}
        isInSelectionMode={isInSelectionMode}
        zoom={zoom}
        setOutput={(b) =>
          appendSelectedRegionOfInterest(
            new UIRegionOfInterest(
              uuidv4(),
              selectedFile.id,
              selectedPageNumber,
              b,
              regionOfInterestContentType,
              selectedLabel,
            ),
          )
        }
      />
      <RegionsOfInterestDisplayLayer
        width={canvasWidth * zoom}
        height={canvasHeight * zoom}
        regionsOfInterest={getRegionsForPage(selectedPageNumber) || []}
        highlighted={focusedRegionOfInterest}
        zoom={zoom}
      />
    </Box>
  )
}
